import React, { useState, useEffect, useMemo, lazy, Suspense } from "react";
import {
  Paper,
  Group,
  Stack,
  Text,
  Box,
  createStyles,
  ActionIcon,
  Tooltip,
  Badge,
} from "@mantine/core";
import {
  Sun,
  Moon,
  Pencil,
  AlertCircle,
  ArrowUpRight,
  UserSearch,
  Paperclip,
  Check,
  X,
  ChevronDown,
  LogOut,
  MapPin,
} from "lucide-react";
import { Button as JemButton, Tag } from "@jem-open/jem-ui";
import { useNavigate, useParams } from "react-router-dom";
import { EmployeeShiftRecord } from "../services/model/EmployeeShiftRecord";
import StyledBadge from "../elements/StyledBadge";
import TimelineMarker from "./TimelineMarker";
import {
  LateCategory,
  getLateCategory,
  getTimelinePosition,
  formatTimeShort,
  generateHourMarkers,
  LATE_CATEGORY_COLORS,
  getShiftLabel,
  getStatusBadge,
  computeActualHours,
  getOverrideSourceLabel,
  padTime,
  isShiftActive,
  hasShiftEnded,
} from "../utils/attendanceUtils";

// Lazy-loaded so the Google Maps bundle only loads when a user actually opens a
// clock-location map, not for every attendance row that renders.
const ClockLocationModal = lazy(() => import("./ClockLocationModal"));

const OVERRIDE_BADGE_STYLE = {
  backgroundColor: "rgba(6, 33, 51, 0.1)",
  color: "#829099",
  border: "none",
  borderRadius: 3,
  textTransform: "none" as const,
  fontWeight: 500,
};

const getStatusDotColor = (
  employee: EmployeeShiftRecord,
  lateThreshold: number
): string | null => {
  // No show → red
  if (employee.status === "no_show") return "#EE0626";

  // Clocked in and late (beyond threshold) → yellow
  if (
    employee.clock_in_time &&
    employee.late_minutes != null &&
    employee.late_minutes > lateThreshold
  ) {
    return "#FFD43B";
  }

  // Clocked in and on time → green
  if (employee.clock_in_time) return "#14926D";

  // Not clocked in: shift ended → red (not coming)
  if (hasShiftEnded(employee.shift_start, employee.shift_end)) return "#EE0626";

  // Not clocked in: shift not ended → grey (not onsite yet)
  if (employee.shift_start && employee.shift_end) return "#CECECE";

  // No data → no dot
  return null;
};

interface EmployeeTimelineRowProps {
  employee: EmployeeShiftRecord;
  shiftStart: string;
  shiftEnd: string;
  lateThreshold?: number;
  locationId?: string;
  /** Geofence centre + radius for the site, used to draw the allowed area on
   *  the clock-location map. Optional — pins still render without it. */
  locationLatitude?: number | null;
  locationLongitude?: number | null;
  locationRadius?: number | null;
}

const useStyles = createStyles((theme) => ({
  row: {
    transition: "box-shadow 150ms ease",
    "&:hover": {
      boxShadow: theme.shadows.sm,
    },
  },
  timeline: {
    position: "relative",
    height: 8,
    backgroundColor: theme.colors.gray[2],
    borderRadius: 4,
  },
  activeBar: {
    position: "absolute",
    top: 0,
    height: "100%",
    borderRadius: 4,
    transition: "width 0.3s ease",
  },
  lateBar: {
    position: "absolute",
    top: 0,
    height: "100%",
    borderRadius: "4px 0 0 4px",
  },
  hourMarkers: {
    marginTop: 2,
  },
  rowHover: {
    "&:hover .action-buttons": {
      opacity: 1,
    },
  },
  onSiteDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.colors.green[6],
  },
  statusDot: {
    width: 11,
    height: 11,
    borderRadius: "50%",
    flexShrink: 0,
  },
}));

const EmployeeTimelineRow: React.FC<EmployeeTimelineRowProps> = ({
  employee,
  shiftStart,
  shiftEnd,
  lateThreshold = 0,
  locationId: locationIdProp,
  locationLatitude,
  locationLongitude,
  locationRadius,
}) => {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const { locationId: routeLocationId } = useParams<{ locationId: string }>();
  const locationId = locationIdProp || routeLocationId;

  // Map modal — shows where the officer physically clocked in / out.
  const [mapModal, setMapModal] = useState<null | "clock-in" | "clock-out">(
    null
  );
  const hasClockInCoords =
    employee.clock_in_latitude != null && employee.clock_in_longitude != null;
  const hasClockOutCoords =
    employee.clock_out_latitude != null && employee.clock_out_longitude != null;
  const geofence = {
    latitude: locationLatitude ?? null,
    longitude: locationLongitude ?? null,
    radius: locationRadius ?? null,
  };
  const category = getLateCategory(employee, lateThreshold);
  const isLate =
    category === LateCategory.LATE_MINOR ||
    category === LateCategory.LATE_MAJOR;
  const statusBadge = getStatusBadge(employee, {
    showMinutes: true,
    thresholdMinutes: lateThreshold,
  });
  const shiftLabel = getShiftLabel(shiftStart, shiftEnd);
  const shiftHasStarted = isShiftActive(shiftStart, shiftEnd);
  const isOnSite = employee.clock_in_time && !employee.clock_out_time;
  const [expanded, setExpanded] = useState(false);

  // Live-updating current time — only ticks when expanded and on-site
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!isOnSite || !expanded) return undefined;
    const interval = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(interval);
  }, [isOnSite, expanded]);

  // Timeline computations — only computed when expanded
  const timelineData = useMemo(() => {
    if (!expanded) return null;
    const timelineStart = padTime(shiftStart, -60);
    const timelineEnd = padTime(shiftEnd, 60);
    const markers = generateHourMarkers(timelineStart, timelineEnd);
    const hours = computeActualHours(
      employee.clock_in_time,
      employee.clock_out_time
    );
    const ciPos = employee.clock_in_time
      ? getTimelinePosition(employee.clock_in_time, timelineStart, timelineEnd)
      : null;
    const coPos = employee.clock_out_time
      ? getTimelinePosition(employee.clock_out_time, timelineStart, timelineEnd)
      : null;
    const currentTime = `${now.getHours().toString().padStart(2, "0")}:${now
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
    const currentPos = getTimelinePosition(
      currentTime,
      timelineStart,
      timelineEnd
    );
    const bStart = ciPos ?? 0;
    const bEnd = coPos ?? (employee.clock_in_time ? currentPos : 0);
    return {
      timelineStart,
      timelineEnd,
      hourMarkers: markers,
      actualHours: hours,
      clockInPos: ciPos,
      clockOutPos: coPos,
      barStart: bStart,
      barWidth: Math.max(0, bEnd - bStart),
    };
  }, [
    expanded,
    shiftStart,
    shiftEnd,
    employee.clock_in_time,
    employee.clock_out_time,
    now,
  ]);

  // Cache override labels to avoid redundant computation
  const clockInOverrideLabel = getOverrideSourceLabel(employee.clock_in_source);
  const clockOutOverrideLabel = getOverrideSourceLabel(
    employee.clock_out_source
  );

  const isNoShowOrNoData =
    employee.status === "not_clocked_in" || employee.status === "no_show";

  const handleFindReplacement = (): void => {
    navigate(
      `/time-attendance/${locationId}/employee/${employee.employee_id}?${
        employee.shift_config_id
          ? `shift_config=${employee.shift_config_id}&`
          : ""
      }tab=replacement`
    );
  };

  const handleEdit = (): void => {
    navigate(
      `/time-attendance/${locationId}/employee/${employee.employee_id}${
        employee.shift_config_id
          ? `?shift_config=${employee.shift_config_id}`
          : ""
      }`
    );
  };

  // Clock out an on-site officer directly from their row. Routed to the
  // override page preset to clock-out (which sends the employee's own
  // shift_config). Shown whenever the officer is on site — deliberately NOT
  // gated on the shift still being active, so an officer lingering past their
  // shift end can still be clocked out.
  const handleClockOut = (): void => {
    navigate(
      `/time-attendance/${locationId}/employee/${employee.employee_id}?${
        employee.shift_config_id
          ? `shift_config=${employee.shift_config_id}&`
          : ""
      }action=clock_out`
    );
  };

  const phoneShiftIcon = ((): JSX.Element => {
    if (shiftLabel === "Day Shift") return <Sun size={14} />;
    if (shiftLabel === "Evening Shift")
      return <Sun size={14} color="#F59E0B" />;
    return <Moon size={14} />;
  })();

  const phoneTimelinePercent = ((): number | null => {
    if (!employee.clock_in_time) return null;
    const startTotal =
      Number(shiftStart.split(":")[0]) * 60 +
      Number(shiftStart.split(":")[1] || 0);
    const endTotal =
      Number(shiftEnd.split(":")[0]) * 60 + Number(shiftEnd.split(":")[1] || 0);
    const total = endTotal - startTotal;
    if (total <= 0) return null;
    const ref = employee.clock_out_time ?? employee.clock_in_time;
    const refTotal =
      Number(ref.split(":")[0]) * 60 + Number(ref.split(":")[1] || 0);
    const pct = Math.round(((refTotal - startTotal) / total) * 100);
    return Math.min(100, Math.max(0, pct));
  })();

  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      className={cx(classes.row, classes.rowHover)}
      style={
        employee.is_replaced
          ? { backgroundColor: "rgba(238, 6, 38, 0.03)" }
          : undefined
      }
    >
      {/* Desktop branch — hidden on phone */}
      <div className="hidden sm:block">
        {/* Header: Employee info + inline CI/CO + status + actions + chevron */}
        <Group position="apart" align="center" spacing="md" noWrap>
          {/* Left: status dot + name + replacement info + inline CI/CO */}
          <Group
            spacing={8}
            align="center"
            noWrap
            style={{ flex: 1, minWidth: 0 }}
          >
            {(() => {
              const dotColor = getStatusDotColor(employee, lateThreshold);
              if (!dotColor) return null;
              return (
                <Box
                  className={classes.statusDot}
                  style={{ backgroundColor: dotColor }}
                />
              );
            })()}
            <Text size="sm" weight={600} style={{ whiteSpace: "nowrap" }}>
              {employee.employee_name}
            </Text>
            {/* Replacement info inline */}
            {employee.is_replaced && employee.replaced_by_employee_name && (
              <Group spacing={6} noWrap align="center">
                <ArrowUpRight size={14} color="#829099" />
                <Text
                  size="xs"
                  style={{ color: "#829099", whiteSpace: "nowrap" }}
                >
                  Replaced by
                </Text>
                <Box
                  className={classes.statusDot}
                  style={{ backgroundColor: "#14926D" }}
                />
                <Text size="sm" weight={600} style={{ whiteSpace: "nowrap" }}>
                  {employee.replaced_by_employee_name}
                </Text>
              </Group>
            )}
            {/* Inline CI/CO info (visible in both states) */}
            {!employee.is_replaced && employee.clock_in_time && (
              <Group spacing={4} noWrap style={{ flexShrink: 0 }}>
                <Text size="xs" color="dimmed">
                  CI: {formatTimeShort(employee.clock_in_time)}
                </Text>
                {clockInOverrideLabel && (
                  <Badge size="xs" style={OVERRIDE_BADGE_STYLE}>
                    {clockInOverrideLabel}
                  </Badge>
                )}
                {employee.clock_out_time && (
                  <>
                    <Text size="xs" color="dimmed">
                      | CO: {formatTimeShort(employee.clock_out_time)}
                    </Text>
                    {clockOutOverrideLabel && (
                      <Badge size="xs" style={OVERRIDE_BADGE_STYLE}>
                        {clockOutOverrideLabel}
                      </Badge>
                    )}
                  </>
                )}
              </Group>
            )}
          </Group>

          {/* Right: status indicators + actions + chevron */}
          <Group spacing="sm" noWrap style={{ flexShrink: 0 }}>
            {employee.is_reliever ||
            employee.shift_code?.toUpperCase() === "O" ? (
              <StyledBadge
                variant="neutral"
                label={
                  employee.is_unscheduled
                    ? "Reliever (unscheduled)"
                    : "Reliever"
                }
              />
            ) : (
              employee.is_unscheduled && (
                <StyledBadge variant="neutral" label="Unscheduled" />
              )
            )}
            {employee.is_replacement && (
              <Tag variant="success">Replacement</Tag>
            )}
            {employee.valid_location === false && (
              <StyledBadge variant="unverified" label="Unverified location" />
            )}
            {employee.is_replaced ? (
              <>
                <Tag variant="failed">{statusBadge.label}</Tag>
                <Tag variant="success">Replacement</Tag>
              </>
            ) : (
              <StyledBadge
                variant={statusBadge.variant}
                label={statusBadge.label}
              />
            )}
            {shiftHasStarted &&
              isNoShowOrNoData &&
              !employee.is_replaced &&
              !employee.is_replacement && (
                <ActionIcon
                  variant="light"
                  size="sm"
                  color="orange"
                  aria-label="Find replacement"
                  title="Find replacement"
                  onClick={() =>
                    navigate(
                      `/time-attendance/${locationId}/employee/${
                        employee.employee_id
                      }?${
                        employee.shift_config_id
                          ? `shift_config=${employee.shift_config_id}&`
                          : ""
                      }tab=replacement`
                    )
                  }
                >
                  <UserSearch size={16} />
                </ActionIcon>
              )}
            {isOnSite && !employee.is_replaced && (
              <ActionIcon
                variant="light"
                size="sm"
                color="red"
                aria-label="Clock out"
                title="Clock out"
                onClick={handleClockOut}
              >
                <LogOut size={16} />
              </ActionIcon>
            )}
            {shiftHasStarted && (
              <ActionIcon
                variant="light"
                size="sm"
                aria-label="Edit employee override"
                title="Edit employee override"
                onClick={() =>
                  navigate(
                    `/time-attendance/${locationId}/employee/${
                      employee.employee_id
                    }${
                      employee.shift_config_id
                        ? `?shift_config=${employee.shift_config_id}`
                        : ""
                    }`
                  )
                }
              >
                <Pencil size={16} />
              </ActionIcon>
            )}
            <ActionIcon
              variant="light"
              size="sm"
              onClick={() => setExpanded((prev) => !prev)}
              aria-label={expanded ? "Collapse" : "Expand"}
            >
              <ChevronDown
                size={20}
                style={{
                  transition: "transform 200ms",
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </ActionIcon>
          </Group>
        </Group>

        {/* Expandable: Timeline + Clock details */}
        {expanded && timelineData && (
          <>
            {/* Shift label */}
            <Group spacing={4} mt="xs">
              {(() => {
                if (shiftLabel === "Day Shift") return <Sun size={14} />;
                if (shiftLabel === "Evening Shift")
                  return <Sun size={14} color="#F59E0B" />;
                return <Moon size={14} />;
              })()}
              <Text size="xs" color="dimmed">
                {shiftLabel}: {formatTimeShort(shiftStart)} -{" "}
                {formatTimeShort(shiftEnd)}
              </Text>
            </Group>

            {/* Timeline section */}
            <Box mt="sm">
              <Box className={classes.timeline}>
                {/* Late bar (amber) */}
                {isLate && timelineData.clockInPos !== null && (
                  <Box
                    className={classes.lateBar}
                    style={{
                      left: "0%",
                      width: `${timelineData.clockInPos}%`,
                      backgroundColor: LATE_CATEGORY_COLORS.late_minor,
                    }}
                  />
                )}
                {/* Active bar (green) */}
                {timelineData.clockInPos !== null && (
                  <Box
                    className={classes.activeBar}
                    style={{
                      left: `${timelineData.barStart}%`,
                      width: `${timelineData.barWidth}%`,
                      backgroundColor: LATE_CATEGORY_COLORS.on_time,
                      borderRadius: isLate ? "0 4px 4px 0" : 4,
                    }}
                  />
                )}
                {/* Clock-in marker */}
                {timelineData.clockInPos !== null && (
                  <TimelineMarker
                    type="clock-in"
                    time={employee.clock_in_time || ""}
                    position={timelineData.clockInPos}
                    late={isLate}
                  />
                )}
                {/* Clock-out marker */}
                {timelineData.clockOutPos !== null && (
                  <TimelineMarker
                    type="clock-out"
                    time={employee.clock_out_time || ""}
                    position={timelineData.clockOutPos}
                  />
                )}
              </Box>
              {/* Hour labels */}
              <Group
                position="apart"
                spacing={0}
                className={classes.hourMarkers}
              >
                {timelineData.hourMarkers.map((marker: string, idx: number) => (
                  <Text
                    key={marker}
                    size="xs"
                    color="dimmed"
                    style={{
                      textAlign: (() => {
                        if (idx === 0) return "left";
                        if (idx === timelineData.hourMarkers.length - 1)
                          return "right";
                        return "center";
                      })(),
                    }}
                  >
                    {formatTimeShort(marker)}
                  </Text>
                ))}
              </Group>
            </Box>

            {/* Clock details */}
            <Stack mt="xs" spacing={4}>
              {employee.clock_in_time ? (
                <>
                  <Group spacing={4} align="center" noWrap>
                    <Text size="xs" color="dimmed">
                      Clocked in: {formatTimeShort(employee.clock_in_time)}
                      {clockInOverrideLabel && (
                        <>
                          <Badge size="xs" ml={4} style={OVERRIDE_BADGE_STYLE}>
                            {clockInOverrideLabel}
                          </Badge>
                          {employee.clock_in_override_reason && (
                            <Tooltip
                              label={`Reason: ${employee.clock_in_override_reason}`}
                              wrapLines
                              width={250}
                            >
                              <AlertCircle
                                size={14}
                                color="#9CA3AF"
                                style={{ cursor: "help", marginLeft: 2 }}
                              />
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Text>
                    {hasClockInCoords && (
                      <Tooltip label="View clock-in location on map">
                        <ActionIcon
                          variant="light"
                          size="sm"
                          color="teal"
                          aria-label="View clock-in location on map"
                          onClick={() => setMapModal("clock-in")}
                        >
                          <MapPin size={14} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                  {employee.clock_in_verification_photo_url && (
                    <Group spacing={6} mt={2}>
                      <Group spacing={4}>
                        <Paperclip size={12} color="#6B7280" />
                        <Text
                          size="xs"
                          color="blue"
                          component="a"
                          href={employee.clock_in_verification_photo_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          img.jpg
                        </Text>
                      </Group>
                      {employee.clock_in_approval_status === "approved" && (
                        <Group spacing={2}>
                          <Check size={12} color="#14926D" />
                          <Text size="xs" color="#14926D" weight={500}>
                            Approved
                          </Text>
                        </Group>
                      )}
                      {employee.clock_in_approval_status === "declined" && (
                        <Group spacing={2}>
                          <X size={12} color="#C53030" />
                          <Text size="xs" color="#C53030" weight={500}>
                            Declined
                          </Text>
                        </Group>
                      )}
                    </Group>
                  )}
                  {employee.clock_out_time ? (
                    <Group spacing={4} align="center" noWrap>
                      <Text size="xs" color="dimmed">
                        Clocked out: {formatTimeShort(employee.clock_out_time)}
                        {timelineData.actualHours !== null &&
                          ` (Actual hours: ${timelineData.actualHours})`}
                        {clockOutOverrideLabel && (
                          <>
                            <Badge
                              size="xs"
                              ml={4}
                              style={OVERRIDE_BADGE_STYLE}
                            >
                              {clockOutOverrideLabel}
                            </Badge>
                            {employee.clock_out_override_reason && (
                              <Tooltip
                                label={`Reason: ${employee.clock_out_override_reason}`}
                                wrapLines
                                width={250}
                              >
                                <AlertCircle
                                  size={14}
                                  color="#9CA3AF"
                                  style={{ cursor: "help", marginLeft: 2 }}
                                />
                              </Tooltip>
                            )}
                          </>
                        )}
                      </Text>
                      {hasClockOutCoords && (
                        <Tooltip label="View clock-out location on map">
                          <ActionIcon
                            variant="light"
                            size="sm"
                            color="teal"
                            aria-label="View clock-out location on map"
                            onClick={() => setMapModal("clock-out")}
                          >
                            <MapPin size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  ) : (
                    <Text size="xs" color="dimmed">
                      Still on site
                    </Text>
                  )}
                </>
              ) : (
                <Text size="xs" color="dimmed">
                  Not clocked in
                </Text>
              )}
            </Stack>
          </>
        )}
      </div>

      {/* Phone branch — sm:hidden, collapsed by default */}
      <div className="sm:hidden flex flex-col gap-xxs">
        {/* Collapsed row: status dot + name + chevron */}
        <div className="flex items-center justify-between gap-sm">
          <div className="flex items-start gap-xs min-w-0 flex-1">
            {(() => {
              const dotColor = getStatusDotColor(employee, lateThreshold);
              if (!dotColor) return null;
              return (
                <span
                  className="inline-block w-2 h-2 rounded-full shrink-0 mt-[6px]"
                  style={{ backgroundColor: dotColor }}
                  aria-hidden="true"
                />
              );
            })()}
            <p className="text-sm font-semibold text-greyscale-text-title break-words m-0">
              {employee.employee_name}
            </p>
          </div>
          <ActionIcon
            variant="light"
            onClick={() => setExpanded((prev) => !prev)}
            aria-label={expanded ? "Collapse" : "Expand"}
            sx={{ width: 44, height: 44, flexShrink: 0 }}
          >
            <ChevronDown
              size={20}
              style={{
                transition: "transform 200ms",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </ActionIcon>
        </div>
        {(employee.clock_in_time || employee.clock_out_time) && (
          <div className="flex flex-wrap items-center gap-xxs text-xs text-greyscale-text-caption">
            {employee.clock_in_time && (
              <span className="inline-flex items-center gap-xxs">
                CI: {formatTimeShort(employee.clock_in_time)}
                {hasClockInCoords && (
                  <button
                    type="button"
                    aria-label="View clock-in location on map"
                    className="inline-flex text-teal-600"
                    onClick={() => setMapModal("clock-in")}
                  >
                    <MapPin size={12} />
                  </button>
                )}
              </span>
            )}
            {employee.clock_in_time && employee.clock_out_time && (
              <span className="mx-xs">|</span>
            )}
            {employee.clock_out_time && (
              <span className="inline-flex items-center gap-xxs">
                CO: {formatTimeShort(employee.clock_out_time)}
                {hasClockOutCoords && (
                  <button
                    type="button"
                    aria-label="View clock-out location on map"
                    className="inline-flex text-teal-600"
                    onClick={() => setMapModal("clock-out")}
                  >
                    <MapPin size={12} />
                  </button>
                )}
              </span>
            )}
          </div>
        )}

        {expanded && (
          <div className="flex flex-col gap-xs mt-xs">
            <div className="flex items-center gap-xs text-xs text-greyscale-text-caption">
              {phoneShiftIcon}
              <span>
                {shiftLabel}: {formatTimeShort(shiftStart)} -{" "}
                {formatTimeShort(shiftEnd)}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-xs">
              {employee.is_reliever ||
              employee.shift_code?.toUpperCase() === "O" ? (
                <StyledBadge
                  variant="neutral"
                  label={
                    employee.is_unscheduled
                      ? "Reliever (unscheduled)"
                      : "Reliever"
                  }
                />
              ) : (
                employee.is_unscheduled && (
                  <StyledBadge variant="neutral" label="Unscheduled" />
                )
              )}
              {employee.is_replacement && (
                <Tag variant="success">Replacement</Tag>
              )}
              {employee.valid_location === false && (
                <StyledBadge variant="unverified" label="Unverified location" />
              )}
              {employee.is_replaced ? (
                <>
                  <Tag variant="failed">{statusBadge.label}</Tag>
                  <Tag variant="success">Replacement</Tag>
                </>
              ) : (
                <StyledBadge
                  variant={statusBadge.variant}
                  label={statusBadge.label}
                />
              )}
            </div>

            {employee.is_replaced && employee.replaced_by_employee_name && (
              <div className="flex items-center gap-xs text-xs text-greyscale-text-caption">
                <ArrowUpRight size={14} color="#829099" />
                <span>Replaced by</span>
                <Box
                  className={classes.statusDot}
                  style={{ backgroundColor: "#14926D" }}
                  aria-hidden
                />
                <span className="text-greyscale-text-title font-semibold">
                  {employee.replaced_by_employee_name}
                </span>
              </div>
            )}

            {isOnSite && !employee.is_replaced && (
              <JemButton
                variant="outline"
                onClick={handleClockOut}
                aria-label="Clock out"
                leftIcon={<LogOut size={16} />}
                className="w-full justify-center border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                Clock out
              </JemButton>
            )}
            {shiftHasStarted && (
              <div className="flex flex-col gap-xs">
                {isNoShowOrNoData &&
                  !employee.is_replaced &&
                  !employee.is_replacement && (
                    <JemButton
                      variant="outline"
                      onClick={handleFindReplacement}
                      aria-label="Find replacement"
                      leftIcon={<UserSearch size={16} />}
                      className="w-full justify-center border-orange-500 text-orange-500 hover:bg-orange-50 hover:text-orange-600"
                    >
                      Find replacement
                    </JemButton>
                  )}
                <JemButton
                  variant="outline"
                  onClick={handleEdit}
                  aria-label="Edit employee override"
                  leftIcon={<Pencil size={16} />}
                  className="w-full justify-center"
                >
                  Edit
                </JemButton>
              </div>
            )}

            {/* Compact timeline */}
            <div className="flex flex-col gap-xxs mt-xs">
              <div className="flex items-center justify-between text-xs text-greyscale-text-caption">
                <span>
                  {formatTimeShort(shiftStart)} - {formatTimeShort(shiftEnd)}
                </span>
                {phoneTimelinePercent !== null && (
                  <span>{phoneTimelinePercent}% through shift</span>
                )}
              </div>
              {timelineData && (
                <Box className={classes.timeline}>
                  {timelineData.clockInPos !== null && (
                    <Box
                      className={classes.activeBar}
                      style={{
                        left: `${timelineData.barStart}%`,
                        width: `${timelineData.barWidth}%`,
                        backgroundColor: LATE_CATEGORY_COLORS.on_time,
                        borderRadius: 4,
                      }}
                    />
                  )}
                </Box>
              )}
            </div>
          </div>
        )}
      </div>

      {mapModal &&
        (() => {
          const isClockIn = mapModal === "clock-in";
          const rawTime = isClockIn
            ? employee.clock_in_time
            : employee.clock_out_time;
          const lat = isClockIn
            ? employee.clock_in_latitude
            : employee.clock_out_latitude;
          const lng = isClockIn
            ? employee.clock_in_longitude
            : employee.clock_out_longitude;
          return (
            <Suspense fallback={null}>
              <ClockLocationModal
                open
                onClose={() => setMapModal(null)}
                clockType={mapModal}
                employeeName={employee.employee_name}
                clockTime={rawTime ? formatTimeShort(rawTime) : null}
                latitude={lat ?? null}
                longitude={lng ?? null}
                geofence={geofence}
              />
            </Suspense>
          );
        })()}
    </Paper>
  );
};

export default EmployeeTimelineRow;
